import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Image, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlayCircleOutlined, PlusOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { del, get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import HlsMedia from '../../components/HlsMedia';
import './SleepVideoPage.css';

const { TextArea } = Input;

interface SleepVideo {
  id: number;
  title: string;
  category: string;
  channel: string;
  video_url: string;
  audio_url: string;
  cover_url: string;
  source_url: string;
  source_platform: string;
  license_name: string;
  artist: string;
  duration_seconds: number;
  sort: number;
  status: number;
  description: string;
}

const categoryOptions = [
  { label: '站内拉取', value: 'site_pull' },
  { label: 'ASMR', value: 'asmr' },
  { label: '自然环境', value: 'nature' },
  { label: '白噪音', value: 'noise' },
  { label: '冥想', value: 'meditation' },
  { label: '其他', value: 'other' },
];

// 用户端首页频道分类:决定资源在 App「电台」页归到哪个分类按钮下
const channelOptions = [
  { label: '起床铃声', value: 'wake' },
  { label: '晚安哄睡', value: 'sleep' },
  { label: '男声陪伴', value: 'male' },
  { label: '女声陪伴', value: 'female' },
];

const formatDuration = (seconds: number) => {
  if (!seconds) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

const SleepVideoPage: React.FC = () => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<SleepVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<SleepVideo | null>(null);
  const [previewing, setPreviewing] = useState<SleepVideo | null>(null);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [categorySavingIds, setCategorySavingIds] = useState<number[]>([]);
  const [channelSavingIds, setChannelSavingIds] = useState<number[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await get<SleepVideo[]>(API_ENDPOINTS.storage.sleepVideos);
      setItems(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载助眠视频失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      title: '',
      category: 'site_pull',
      channel: 'sleep',
      video_url: '',
      audio_url: '',
      cover_url: '',
      source_url: '',
      source_platform: '',
      license_name: '',
      artist: '',
      duration_seconds: 0,
      sort: 100,
      status: 1,
      description: '',
    });
    setModalOpen(true);
  };

  const openEdit = (record: SleepVideo) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await put(API_ENDPOINTS.storage.sleepVideoDetail(editing.id), values);
      } else {
        await post(API_ENDPOINTS.storage.sleepVideos, values);
      }
      message.success('助眠视频已保存');
      setModalOpen(false);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存助眠视频失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (record: SleepVideo, checked: boolean) => {
    try {
      await put(API_ENDPOINTS.storage.sleepVideoDetail(record.id), { status: checked ? 1 : 0 });
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新状态失败');
    }
  };

  const updateCategory = async (record: SleepVideo, category: string) => {
    if (category === record.category) return;

    setItems((current) => current.map((item) => (
      item.id === record.id ? { ...item, category } : item
    )));
    setCategorySavingIds((current) => [...current, record.id]);
    try {
      await put(API_ENDPOINTS.storage.sleepVideoDetail(record.id), { category });
      message.success('分类已保存');
    } catch (error) {
      setItems((current) => current.map((item) => (
        item.id === record.id ? { ...item, category: record.category } : item
      )));
      message.error(error instanceof Error ? error.message : '分类保存失败');
    } finally {
      setCategorySavingIds((current) => current.filter((id) => id !== record.id));
    }
  };

  const updateChannel = async (record: SleepVideo, channel: string) => {
    if (channel === record.channel) return;

    setItems((current) => current.map((item) => (
      item.id === record.id ? { ...item, channel } : item
    )));
    setChannelSavingIds((current) => [...current, record.id]);
    try {
      await put(API_ENDPOINTS.storage.sleepVideoDetail(record.id), { channel });
      message.success('首页频道已保存');
    } catch (error) {
      setItems((current) => current.map((item) => (
        item.id === record.id ? { ...item, channel: record.channel } : item
      )));
      message.error(error instanceof Error ? error.message : '首页频道保存失败');
    } finally {
      setChannelSavingIds((current) => current.filter((id) => id !== record.id));
    }
  };

  const deleteItem = async (record: SleepVideo) => {
    try {
      await del(API_ENDPOINTS.storage.sleepVideoDetail(record.id));
      message.success('助眠视频已删除');
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除助眠视频失败');
    }
  };

  const bulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        selectedRowKeys.map((id) => del(API_ENDPOINTS.storage.sleepVideoDetail(Number(id)))),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = results.length - failed;
      if (ok) message.success(`已删除 ${ok} 条${failed ? `，${failed} 条失败` : ''}`);
      else message.error('批量删除失败');
      setSelectedRowKeys([]);
      await fetchItems();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '批量删除失败');
    } finally {
      setBulkDeleting(false);
    }
  };

  const refreshMedia = async (record: SleepVideo) => {
    setRefreshingId(record.id);
    try {
      const updated = await post<SleepVideo>(API_ENDPOINTS.storage.sleepVideoRefreshMedia(record.id));
      setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      if (updated.video_url || updated.audio_url) {
        message.success('媒体地址已补齐');
      } else {
        message.warning('来源平台当前未公开可播放直链');
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '补齐媒体失败');
    } finally {
      setRefreshingId(null);
    }
  };

  const openPreview = async (record: SleepVideo) => {
    if (record.video_url || record.audio_url) {
      setPreviewing(record);
      return;
    }
    setRefreshingId(record.id);
    try {
      const updated = await post<SleepVideo>(API_ENDPOINTS.storage.sleepVideoRefreshMedia(record.id));
      setItems((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      if (updated.video_url || updated.audio_url) {
        setPreviewing(updated);
      } else {
        message.warning('当前只拉取到了封面和来源信息，平台未公开可播放的视频直链');
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '补齐媒体失败');
    } finally {
      setRefreshingId(null);
    }
  };

  const columns: ColumnsType<SleepVideo> = [
    {
      title: '视频',
      dataIndex: 'title',
      render: (_, record) => (
        <div className="sleep-video-title-cell">
          <button className="sleep-video-cover-button" type="button" onClick={() => openPreview(record)} disabled={refreshingId === record.id}>
            {record.cover_url ? <Image width={112} height={63} src={record.cover_url} preview={false} /> : <div className="sleep-video-cover-empty" />}
            <span className="sleep-video-cover-play">
              {refreshingId === record.id ? <SyncOutlined spin /> : <PlayCircleOutlined />}
            </span>
          </button>
          <div>
            <strong>{record.title}</strong>
            <span>{record.description || record.artist || '暂无描述'}</span>
          </div>
        </div>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      width: 140,
      render: (value, record) => (
        <Select
          aria-label={`设置${record.title}分类`}
          value={value}
          options={categoryOptions}
          loading={categorySavingIds.includes(record.id)}
          disabled={categorySavingIds.includes(record.id)}
          onChange={(category) => updateCategory(record, category)}
          popupMatchSelectWidth={false}
          style={{ width: 110 }}
        />
      ),
    },
    {
      title: '首页频道',
      dataIndex: 'channel',
      width: 140,
      render: (value, record) => (
        <Select
          aria-label={`设置${record.title}首页频道`}
          value={value || 'sleep'}
          options={channelOptions}
          loading={channelSavingIds.includes(record.id)}
          disabled={channelSavingIds.includes(record.id)}
          onChange={(channel) => updateChannel(record, channel)}
          popupMatchSelectWidth={false}
          style={{ width: 110 }}
        />
      ),
    },
    {
      title: '来源',
      width: 180,
      render: (_, record) => (
        <div className="sleep-video-source-cell">
          <span>{record.source_platform || '-'}</span>
          {record.source_url && <a href={record.source_url} target="_blank" rel="noreferrer">来源页</a>}
        </div>
      ),
    },
    {
      title: '作者',
      dataIndex: 'artist',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: '时长',
      dataIndex: 'duration_seconds',
      width: 100,
      render: formatDuration,
    },
    {
      title: '媒体',
      width: 170,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.video_url || record.audio_url ? (
            <Button size="small" type="link" icon={<PlayCircleOutlined />} onClick={() => openPreview(record)}>
              播放
            </Button>
          ) : (
            <Tag>平台未公开直链</Tag>
          )}
          <Button
            size="small"
            type="link"
            icon={<SyncOutlined spin={refreshingId === record.id} />}
            loading={refreshingId === record.id}
            onClick={() => refreshMedia(record)}
          >
            补齐媒体
          </Button>
        </Space>
      ),
    },
    {
      title: '启用',
      dataIndex: 'status',
      width: 90,
      render: (value, record) => <Switch checked={value === 1} onChange={(checked) => toggleStatus(record, checked)} />,
    },
    {
      title: '操作',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除这条助眠视频？" onConfirm={() => deleteItem(record)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="sleep-video-page">
      <div className="sleep-video-header">
        <div>
          <h1 className="page-title">助眠视频</h1>
          <p>管理从数据拉取沉淀进来的助眠视频、封面和来源信息。</p>
        </div>
        <Space>
          <Popconfirm
            title={`确认删除选中的 ${selectedRowKeys.length} 条助眠视频？`}
            onConfirm={bulkDelete}
            okText="删除"
            cancelText="取消"
            disabled={!selectedRowKeys.length}
          >
            <Button danger icon={<DeleteOutlined />} disabled={!selectedRowKeys.length} loading={bulkDeleting}>
              批量删除{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={fetchItems}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增视频</Button>
        </Space>
      </div>

      <Card bordered={false}>
        <Table
          rowKey="id"
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal title={editing ? '编辑助眠视频' : '新增助眠视频'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave} confirmLoading={saving} width={820} destroyOnClose>
        <Form form={form} layout="vertical">
          <div className="sleep-video-form-grid">
            <Form.Item label="名称" name="title" rules={[{ required: true, message: '请输入名称' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="分类" name="category" rules={[{ required: true, message: '请选择分类' }]}>
              <Select options={categoryOptions} />
            </Form.Item>
            <Form.Item label="首页频道" name="channel" rules={[{ required: true, message: '请选择频道' }]}>
              <Select options={channelOptions} />
            </Form.Item>
          </div>
          <Form.Item label="视频 URL" name="video_url"><Input placeholder="视频文件地址，可为空" /></Form.Item>
          <Form.Item label="音频 URL" name="audio_url"><Input placeholder="音频文件地址，可为空" /></Form.Item>
          <Form.Item label="封面 URL" name="cover_url"><Input placeholder="封面地址" /></Form.Item>
          <div className="sleep-video-form-grid">
            <Form.Item label="来源 URL" name="source_url"><Input /></Form.Item>
            <Form.Item label="来源平台" name="source_platform"><Input /></Form.Item>
          </div>
          <div className="sleep-video-form-grid">
            <Form.Item label="作者" name="artist"><Input /></Form.Item>
            <Form.Item label="授权" name="license_name"><Input /></Form.Item>
          </div>
          <div className="sleep-video-form-grid">
            <Form.Item label="时长（秒）" name="duration_seconds"><InputNumber min={0} max={86400} style={{ width: '100%' }} /></Form.Item>
            <Form.Item label="排序" name="sort"><InputNumber min={0} max={9999} style={{ width: '100%' }} /></Form.Item>
          </div>
          <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
            <Select options={[{ label: '启用', value: 1 }, { label: '停用', value: 0 }]} />
          </Form.Item>
          <Form.Item label="描述" name="description"><TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title={previewing?.title || '播放助眠视频'}
        open={!!previewing}
        footer={null}
        onCancel={() => setPreviewing(null)}
        width={900}
        destroyOnClose
      >
        {previewing?.video_url ? (
          <HlsMedia kind="video" className="sleep-video-player" src={previewing.video_url} poster={previewing.cover_url} controls preload="metadata" />
        ) : previewing?.audio_url ? (
          <div className="sleep-video-audio-preview">
            {previewing.cover_url ? <Image src={previewing.cover_url} preview={false} /> : null}
            <HlsMedia src={previewing.audio_url} controls preload="metadata" />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default SleepVideoPage;
